import { CustomerDetailsProps, CustomerInfoProps } from "../pages/SalesDocuments-v2";
import { MeterApiData, UnitRate } from "../types/salesDocuments.types";

import {
  ApiResult,
  ZohoMeterRaw,
  retrieveCustomerDetails,
  retrieveMeterDetails,
} from "./zoho.service";

interface CustomerDetails {
  registration_number: string | null;
  type_of_business: string;
  business_address: string[];
}

interface CustomerInfo {
  contact_name: string;
  phone_number?: string;
  email_address: string;
  date_of_birth?: string;
}

interface KvaDetails {
  capacity: number;
  charge: number;
  frequency: string;
}

interface ElectricityData {
  zoho_meter_id: string;
  mpan: string;
  fit_charge?: string;
  fit_charge_unit?: string;
  annual_consumption: number;
  estimated_annual_cost: number;
  consumption_measurement: string;
  estimate_includes_kva: string;
  product_name?: string;
  passthrough?: string;
  non_commodity_cost?: string;
  commission: string;
  day_unit_rate?: UnitRate;
  night_unit_rate?: UnitRate;
  evening_weekend_rate?: UnitRate;
  evening_weekend_night_rate?: UnitRate;
  start_date: string;
  end_date: string;
  new_supplier: string;
  contract_type: string;
  supply_address: string[];
  standing_charge?: number;
  kva?: KvaDetails;
  uplift_rate?: string;
  sc_uplift_rate?: string;
}

interface GasData {
  zoho_meter_id: string;
  mprn: string;
  start_date: string;
  end_date: string;
  new_supplier: string;
  product_name?: string;
  passthrough?: string;
  contract_type: string;
  annual_consumption: number;
  estimated_annual_cost: number;
  supply_address: string[];
  unit_rate: UnitRate;
  non_commodity_cost?: string;
  commission: string;
  uplift_rate?: string;
  sc_uplift_rate?: string;
  standing_charge?: number;
}

interface Quote {
  electricity_data: ElectricityData[];
  gas_data: GasData[];
}

interface SalesDocumentPayload {
  format: "html" | "pdf";
  zoho_id: string;
  company_name: string;
  customer_details: CustomerDetails;
  customer_info: CustomerInfo;
  quote: Quote;
}

const addOneDay = (dateStr: string | null): string | null => {
  if (dateStr === null) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  date.setDate(date.getDate() + 1);

  const newYear = String(date.getFullYear());
  const newMonth = String(date.getMonth() + 1).padStart(2, "0");
  const newDay = String(date.getDate()).padStart(2, "0");

  return `${newYear}-${newMonth}-${newDay}`;
};

const isElectricityMeter = (meterType: string | null): boolean => {
  return meterType?.toLowerCase().includes("elec") ?? false;
};

const mapQueenieDataToMeter = (d: ZohoMeterRaw): MeterApiData => {
  const isElectricity = isElectricityMeter(d.Which_Meter);

  return {
    mpanBottomLine: d.Mpan_Bottom_1,
    mprn: d.MPRN,
    street: d.Street,
    town: d.Town,
    county: d.County,
    country: d.Country,
    postcode: d.Postcode,
    energyType: d.Which_Meter,
    newSupplier: isElectricity ? d.New_Supplier_E1 : d.New_Supplier_G1,
    contractType: isElectricity ? d.Contract_Type : d.Contract_Type_Gas,
    standingCharge: isElectricity ? d.New_SC_E1 : d.New_SC_p_G1,
    startDate: addOneDay(isElectricity ? d.CED_E1 : d.CED_G1),
    endDate: isElectricity ? d.New_CED_E1 : d.New_CED_G1,
    annualConsumption: isElectricity ? d.AQ_1 : d.Gas_AQ_1,
    passthrough: isElectricity ? d.Passthrough_1 : d.Gas_Passthrough,
    commission: d.Amount,
    upliftRate: isElectricity ? d.Uplift_p_E1 : d.Uplift_p_G1,
    scUpliftRate: isElectricity ? d.SC_Uplift_E1_New : d.SC_Uplift_Gas,
    dayUnitRate: isElectricity ? d.New_Day_Rate_E1 : d.New_Unit_Price_p_G1,
    nightUnitRate: d.New_Night_Rate_E1,
    eveningWeekendRate: d.New_Eve_Weekend_p,
    eveningWeekendNightRate: d.New_Evening_weekend_and_night_rate,
    mpanTopLine: d.Mpan_Top_1,
    kvaCharge: d.New_KVA_Charge,
    kvaCapacity: d.KVA_Capacity,
    kvaChargeFrequency: d.KVA_Charge_Frequency,
  };
};

interface FormattedCustomer {
  customerDetails: CustomerDetailsProps;
  customerInfo: CustomerInfoProps;
  companyName: string;
}

export const formatCustomerDetails = async (
  customerId: string,
): Promise<ApiResult<FormattedCustomer>> => {
  const result = await retrieveCustomerDetails(customerId);
  if (!result.success) {
    console.error(`Customer fetch failed (${String(result.statusCode)}):`, result.error);
    return result;
  }
  const d = result.data;
  return {
    success: true,
    data: {
      customerDetails: {
        typeOfBusiness: d.Type_of_Business,
        businessAddress: d.Business_Address?.split(",").map((s) => s.trim()) ?? [],
        registrationNumber: d.Company_Reg_Number ?? null,
      },
      customerInfo: {
        contactName: `${d.First_Name} ${d.Last_Name}`,
        phoneNumber: d.Phone?.replace(/[a-zA-Z]/g, ""),
        emailAddress: d.Email,
        dateOfBirth: undefined,
      },
      companyName: d.Account_Name,
    },
  };
};

export const formatMeterDetails = async (meterId: string): Promise<ApiResult<MeterApiData>> => {
  const result = await retrieveMeterDetails(meterId);
  if (!result.success) {
    console.error(`Meter fetch failed (${String(result.statusCode)}):`, result.error);
    return result;
  }
  return {
    success: true,
    data: mapQueenieDataToMeter(result.data),
  };
};

export type { SalesDocumentPayload, ElectricityData, GasData };
